import { useEffect, useMemo, useState } from 'react'
import { palettePrimaryMain } from '@/theme/designTokens'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate, useParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import OutlinedInput from '@mui/material/OutlinedInput'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import { AlertStatusPanel } from '@/shared/components/confirmation'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { ApiError } from '@/api/http/types'
import { messageFromTeamInvitePasswordError } from '@/shared/lib/apiErrorMessages'
import { useToast } from '@/shared/toast/ToastProvider'
import {
  teamInvitePasswordSchema,
  type TeamInvitePasswordFormData,
} from '@/features/auth/lib/team-invite-password-schema'
import {
  allTeamInvitePasswordRulesMet,
  evaluateTeamInvitePasswordRules,
  TEAM_INVITE_PASSWORD_RULE_LABELS,
  TEAM_INVITE_PASSWORD_RULE_ORDER,
} from '@/features/auth/lib/team-invite-password-rules'
import {
  markPostInviteWelcomePending,
  postInviteWelcomeQuery,
} from '@/features/dashboard/lib/post-invite-welcome'
import { useCompleteTeamInvitation, useValidateTeamInvitationToken } from '../api/useTeamInvitation'
import { InvitePasswordRuleStatusIcon } from '../components/InvitePasswordRuleStatusIcons'
import { passwordVisibilityIconSrc } from '@/shared/lib/password-visibility'
import teamInviteLockPasswordIcon from '@/assets/team-invite-lock-password-icon.svg'

const inputSx = {
  height: 46,
  bgcolor: '#ffffff',
  borderRadius: '10px',
  fontSize: '14px',
  letterSpacing: '-0.1504px',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: palettePrimaryMain,
    borderWidth: '1px',
  },
  '& .MuiOutlinedInput-input': {
    px: '16px',
    py: '12px',
    color: '#08263f',
    '&::placeholder': { color: '#9ca3af', opacity: 1 },
  },
} as const

const headingSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '32px',
  lineHeight: '40px',
  color: '#23262f',
  textAlign: 'center',
} as const

const welcomeSubtitleSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '16px',
  letterSpacing: '-0.4304px',
  color: '#4a5565',
  textAlign: 'center',
  maxWidth: 380,
  mx: 'auto',
} as const

/** Body copy on invalid / success panels */
const subtitleSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  color: '#061e32',
  textAlign: 'center',
} as const

const labelSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '-0.1504px',
  color: '#23262f',
  mb: '8px',
} as const

const primaryButtonSx = {
  bgcolor: palettePrimaryMain,
  borderRadius: '12px',
  height: 48,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '16px',
  lineHeight: '20px',
  color: '#ffffff',
  textTransform: 'none',
  '&:hover': { bgcolor: '#007bb8' },
} as const

function displayRole(role: string): string {
  const u = role.toUpperCase()
  if (u === 'EDITOR') return 'Editor'
  if (u === 'VIEWER') return 'Viewer'
  return role
}

function welcomeMessage(firstName: string, role: string): string {
  return `Welcome ${firstName}! Create a secure password to activate your Sense maker ${displayRole(role)} account`
}

function isInviteLinkError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false
  if (error.status === 404 || error.status === 410) return true
  return error.code === 'not_found' || error.code === 'gone'
}

type Step = 'loading' | 'form' | 'success' | 'invalid'

export function TeamInviteAcceptPage() {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const { token: rawToken } = useParams<{ token: string }>()
  const token = rawToken?.trim() ?? ''

  const [step, setStep] = useState<Step>(() => (token ? 'loading' : 'invalid'))
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const validation = useValidateTeamInvitationToken(token || undefined)
  const complete = useCompleteTeamInvitation()

  useEffect(() => {
    if (!token) {
      setStep('invalid')
    }
  }, [token])

  useEffect(() => {
    if (!token) return
    if (validation.isSuccess) setStep('form')
    if (validation.isError) setStep('invalid')
  }, [token, validation.isSuccess, validation.isError])

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<TeamInvitePasswordFormData>({
    resolver: zodResolver(teamInvitePasswordSchema),
    defaultValues: { password: '', confirmPassword: '' },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })

  const watchedPassword = watch('password') ?? ''
  const watchedConfirm = watch('confirmPassword') ?? ''

  const ruleChecks = useMemo(
    () => evaluateTeamInvitePasswordRules(watchedPassword),
    [watchedPassword],
  )

  const passwordRulesComplete = allTeamInvitePasswordRulesMet(ruleChecks)
  const passwordFieldShowsError =
    watchedPassword.length > 0 && !passwordRulesComplete
  const confirmMismatch =
    watchedConfirm.length > 0 && watchedConfirm !== watchedPassword

  const passwordHelperText =
    errors.password?.message ??
    (watchedPassword.length > 0 && !ruleChecks.minLength
      ? 'Password must be at least 8 characters'
      : null)

  const confirmHelperText =
    errors.confirmPassword?.message ??
    (confirmMismatch ? 'Password do not match' : null)

  const pageTitle: Record<Step, string> = {
    loading: 'Accept invitation',
    form: 'Set Up Your Password',
    success: 'Welcome',
    invalid: 'Link expired',
  }
  useDocumentTitle(pageTitle[step])

  const onSubmit = (data: TeamInvitePasswordFormData) => {
    if (!token) return
    complete.mutate(
      { token, password: data.password },
      {
        onSuccess: () => setStep('success'),
        onError: (error) => {
          if (isInviteLinkError(error)) {
            setStep('invalid')
            return
          }
          showToast(messageFromTeamInvitePasswordError(error), 'error')
        },
      },
    )
  }

  const passwordReg = register('password')
  const confirmReg = register('confirmPassword')

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: '#e1e7f3',
        px: 2,
        py: 3,
      }}
    >
      {step === 'loading' && (
        <CircularProgress size={40} sx={{ color: palettePrimaryMain }} />
      )}

      {step === 'invalid' && (
        <AlertStatusPanel
          headline="Link Expired"
          iconVariant="alert"
          bodyLines={[
            'This invitation link is invalid or has expired',
            'Please contact your administrator for a new link',
          ]}
          primaryLabel="Back to Login"
          onPrimary={() => navigate('/login', { replace: true })}
          panelTestId="team-invite-link-expired"
        />
      )}

      {step === 'form' && validation.data && (
        <Box
          sx={{
            width: '100%',
            maxWidth: 448,
            bgcolor: '#ffffff',
            borderRadius: '10px',
            boxShadow:
              '0px 20px 25px 0px rgba(0,0,0,0.1), 0px 8px 10px 0px rgba(0,0,0,0.1)',
            pt: '32px',
            pb: '32px',
            px: '32px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {/* Figma 4039:454 — 64×64 circle; glyph 31×31 @ (17, 15) */}
          <Box
            sx={{
              width: 64,
              height: 64,
              bgcolor: '#e0f1fe',
              borderRadius: '50%',
              position: 'relative',
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={teamInviteLockPasswordIcon}
              alt=""
              sx={{
                position: 'absolute',
                left: '17px',
                top: '15px',
                width: 31,
                height: 31,
                display: 'block',
              }}
            />
          </Box>

          <Typography component="h1" sx={{ ...headingSx, mt: '24px' }}>
            Set Up Your Password
          </Typography>
          <Typography sx={{ ...welcomeSubtitleSx, mt: '8px' }}>
            {welcomeMessage(validation.data.firstName, validation.data.role)}
          </Typography>

          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit(onSubmit)}
            sx={{
              mt: '32px',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            <Box>
              <Typography sx={labelSx} component="label" htmlFor="team-invite-password">
                Password{' '}
                <Box component="span" sx={{ color: '#ef4444' }}>
                  *
                </Box>
              </Typography>
              <OutlinedInput
                id="team-invite-password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter password"
                fullWidth
                autoComplete="new-password"
                error={passwordFieldShowsError || Boolean(errors.password)}
                inputRef={passwordReg.ref}
                name={passwordReg.name}
                onChange={passwordReg.onChange}
                onBlur={passwordReg.onBlur}
                sx={inputSx}
                aria-describedby={
                  passwordHelperText ? 'team-invite-password-helper' : undefined
                }
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      type="button"
                      edge="end"
                      tabIndex={-1}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPassword((v) => !v)}
                      sx={{ color: '#9ca3af' }}
                    >
                      <Box
                        component="img"
                        src={passwordVisibilityIconSrc(showPassword)}
                        alt=""
                        sx={{ width: 20, height: 16 }}
                      />
                    </IconButton>
                  </InputAdornment>
                }
              />
              {passwordHelperText && (
                <Typography
                  id="team-invite-password-helper"
                  sx={{ color: '#ef4444', fontSize: '14px', mt: '8px', lineHeight: '20px' }}
                >
                  {passwordHelperText}
                </Typography>
              )}
            </Box>

            <Box>
              <Typography sx={labelSx} component="label" htmlFor="team-invite-confirm-password">
                Confirm Password{' '}
                <Box component="span" sx={{ color: '#ef4444' }}>
                  *
                </Box>
              </Typography>
              <OutlinedInput
                id="team-invite-confirm-password"
                type={showConfirm ? 'text' : 'password'}
                placeholder="Re-enter password"
                fullWidth
                autoComplete="new-password"
                error={confirmMismatch || Boolean(errors.confirmPassword)}
                inputRef={confirmReg.ref}
                name={confirmReg.name}
                onChange={confirmReg.onChange}
                onBlur={confirmReg.onBlur}
                sx={inputSx}
                aria-describedby={
                  confirmHelperText ? 'team-invite-confirm-password-helper' : undefined
                }
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      type="button"
                      edge="end"
                      tabIndex={-1}
                      aria-label={showConfirm ? 'Hide password' : 'Show password'}
                      onClick={() => setShowConfirm((v) => !v)}
                      sx={{ color: '#9ca3af' }}
                    >
                      <Box
                        component="img"
                        src={passwordVisibilityIconSrc(showConfirm)}
                        alt=""
                        sx={{ width: 20, height: 16 }}
                      />
                    </IconButton>
                  </InputAdornment>
                }
              />
              {confirmHelperText && (
                <Typography
                  id="team-invite-confirm-password-helper"
                  sx={{ color: '#ef4444', fontSize: '14px', mt: '8px', lineHeight: '20px' }}
                >
                  {confirmHelperText}
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                bgcolor: '#f9fafb',
                borderRadius: '10px',
                px: '12px',
                pt: '12px',
                pb: '12px',
                width: '100%',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: '#4a5565',
                  mb: '4px',
                }}
              >
                Password must contain:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 0, listStyle: 'none' }}>
                {TEAM_INVITE_PASSWORD_RULE_ORDER.map((key) => {
                  const met = ruleChecks[key]
                  const label = TEAM_INVITE_PASSWORD_RULE_LABELS[key]
                  return (
                    <Box
                      key={key}
                      component="li"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        mb: '4px',
                        minHeight: 16,
                        '&:last-child': { mb: 0 },
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 400,
                          fontSize: '12px',
                          lineHeight: '16px',
                          color: '#4a5565',
                          flex: 1,
                          pr: 1,
                        }}
                      >
                        • {label}
                      </Typography>
                      <InvitePasswordRuleStatusIcon met={met} label={label} />
                    </Box>
                  )
                })}
              </Box>
            </Box>

            <Button
              type="submit"
              fullWidth
              disabled={complete.isPending}
              sx={{ ...primaryButtonSx, mt: 0 }}
            >
              {complete.isPending ? 'Saving…' : 'Submit'}
            </Button>
          </Box>
        </Box>
      )}

      {step === 'success' && (
        <Box
          sx={{
            width: '100%',
            maxWidth: 464,
            bgcolor: '#ffffff',
            borderRadius: '16px',
            boxShadow:
              '0px 10px 15px 0px rgba(0,0,0,0.1), 0px 4px 6px 0px rgba(0,0,0,0.1)',
            py: 4,
            px: 4,
            textAlign: 'center',
          }}
        >
          <Typography component="h1" sx={headingSx}>
            You&apos;re all set
          </Typography>
          <Typography sx={{ ...subtitleSx, mt: 2 }}>
            Your account is active. Sign in with your email and the password you just created.
          </Typography>
          <Button
            fullWidth
            sx={{ ...primaryButtonSx, mt: 3 }}
            onClick={() => {
              markPostInviteWelcomePending()
              navigate(`/login?${postInviteWelcomeQuery()}`, { replace: true })
            }}
          >
            Sign in
          </Button>
        </Box>
      )}
    </Box>
  )
}
